import Image from 'next/image';
import { Elements, RichText } from 'prismic-reactjs';
import { Fragment, useEffect, useRef, useState } from 'react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { AnimatedSection, useAnimateSection } from 'remote-com-shared/style/motion';
import SwiperCore, { Autoplay, Pagination } from 'swiper/core';
import { SwiperSlide } from 'swiper/react';

import {
  CarouselSectionWrapper,
  SectionHeader,
  LabelComponent,
  DescriptionComponent,
  SwiperSlider,
  Slide as StyledSlide,
  SlideContentContainer,
  SlideContainer,
  SlideHeader,
  HeaderIcon,
  SlideTitle,
  SlideDescription,
  SlideDescriptionNote,
  SlideDescriptionStrong,
  SlideDescriptionOrderedList,
  SlideIllustration,
  SlideLabelsWrapper,
  SlidesLabels,
  SlideLabel,
  ProgressBarHighlight,
  ProgressBarBackground,
} from './styles';

import 'swiper/swiper.min.css';
import { OverviewTitleComponent } from '.';

SwiperCore.use([Pagination, Autoplay]);

function htmlSerializer(type, element, content, children, index) {
  switch (type) {
    case Elements.strong: {
      return <SlideDescriptionStrong key={index}>{children}</SlideDescriptionStrong>;
    }
    case Elements.preformatted: {
      return <SlideDescriptionNote key={index}>{children}</SlideDescriptionNote>;
    }
    case Elements.oList: {
      return <SlideDescriptionOrderedList key={index}>{children}</SlideDescriptionOrderedList>;
    }

    default:
      return null;
  }
}

function Slide({
  entry: { title, description, illustration, label_icon: icon },
  index,
  directionOffset,
}) {
  const isEven = directionOffset ? index % 2 !== 0 : index % 2 === 0;

  return (
    <StyledSlide>
      <SlideContentContainer $isEven={isEven}>
        <SlideContainer>
          <SlideHeader>
            <HeaderIcon>
              {icon && (
                <Image
                  src={icon.url}
                  alt=""
                  width={icon.dimensions.width}
                  height={icon.dimensions.height}
                  quality="100"
                />
              )}
            </HeaderIcon>
            <RichText render={title} Component={SlideTitle} />
          </SlideHeader>

          <div className="c-section-slider__content">
            <RichText
              render={description}
              Component={SlideDescription}
              htmlSerializer={htmlSerializer}
            />
          </div>
        </SlideContainer>
      </SlideContentContainer>
      {illustration && (
        <SlideIllustration
          src={illustration.url}
          alt=""
          width={illustration.dimensions.width}
          height={illustration.dimensions.height}
          $isEven={isEven}
        />
      )}
    </StyledSlide>
  );
}

const SlidesProgressBar = ({ dirty, active }) => (
  <ProgressBarBackground $dirty={dirty} className={active ? 'active' : null} $active={active}>
    <ProgressBarHighlight />
  </ProgressBarBackground>
);
const SlidesLabelsComponent = ({ slides, activeIndex, visible, manuallySwiped, onClick }) => {
  const [dirty, setDirty] = useState(false);
  function handleClick(params) {
    setDirty(true);
    onClick(params);
  }

  return (
    <SlideLabelsWrapper>
      <SlidesLabels>
        {slides.map((entry, index) => (
          <Fragment key={index}>
            {index ? (
              <SlidesProgressBar
                dirty={dirty || manuallySwiped}
                active={activeIndex === index - 1}
              />
            ) : null}

            <SlideLabel
              key={index}
              $active={activeIndex === index}
              $dirty={dirty || manuallySwiped}
              $visible={visible}
              onClick={() => handleClick(index)}
            >
              {index + 1}
              <p>{RichText.asText(entry.title)}</p>
            </SlideLabel>
          </Fragment>
        ))}
      </SlidesLabels>
    </SlideLabelsWrapper>
  );
};

export const SlidesCarouselSection = ({
  id,
  label,
  title,
  description,
  slides = [],
  directionOffset,
  light = false,
  fontBrand,
  darkTone,
}) => {
  const swiperRef = useRef(null);
  const sectionRef = useRef();
  const isVisible = useAnimateSection(sectionRef);
  const [userDidSwipe, setUserDidSwipe] = useState(false);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  function handleSlideChange(swiper) {
    setActiveSlideIndex(swiper.activeIndex);
  }
  function handleGoToSlideIndex(index) {
    swiperRef.current.swiper.slideTo(index);
  }
  function handleFirstUserSwipe() {
    setUserDidSwipe(true);
  }

  useEffect(() => {
    swiperRef.current.swiper.autoplay.stop();
  }, []);
  useEffect(() => {
    if (isVisible) {
      swiperRef.current.swiper.autoplay.start();
    }
  }, [isVisible]);

  return (
    <CarouselSectionWrapper
      id={id}
      ref={sectionRef}
      $light={light}
      $fontBrand={fontBrand}
      $darkTone={darkTone}
    >
      <AnimatedSection ComponentName={SectionHeader}>
        <ContentContainer>
          {label && (
            <LabelComponent>
              <small>{label}</small>
            </LabelComponent>
          )}
          {title && <RichText render={title} Component={OverviewTitleComponent} />}
          {description && <RichText render={description} Component={DescriptionComponent} />}
        </ContentContainer>
      </AnimatedSection>
      <SlidesLabelsComponent
        slides={slides}
        visible={isVisible}
        manuallySwiped={userDidSwipe}
        onClick={handleGoToSlideIndex}
        activeIndex={activeSlideIndex}
      />
      <SwiperSlider
        grabCursor
        spaceBetween={100}
        autoplay={{ delay: 5000, stopOnLastSlide: true }}
        ref={swiperRef}
        onSlideChange={handleSlideChange}
        onSliderFirstMove={handleFirstUserSwipe}
      >
        {slides.map((entry, index) => (
          <SwiperSlide key={index}>
            <Slide entry={entry} directionOffset={directionOffset} index={index} />
          </SwiperSlide>
        ))}
      </SwiperSlider>
    </CarouselSectionWrapper>
  );
};
